import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { components } from '@trackercop/contracts';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-keys';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScriptCategoryChip } from '@/components/category-chip';
import { ReviewPopover } from '@/components/review-popover';
import { SCRIPT_CATEGORIES, CLASSIFICATION_SOURCES, CMP_PROVIDERS } from '@/lib/constants';

type DetectedScript = components['schemas']['DetectedScript'];
type ConsentMode = components['schemas']['ConsentMode'];
type ReviewMap = Map<string, components['schemas']['FindingReview']>;

interface ScriptsTabProps {
  crawlId: string;
  consentMode: ConsentMode | 'all';
  gtmContainerId: string | 'all';
  reviews: ReviewMap;
}

export function ScriptsTab({ crawlId, consentMode, gtmContainerId, reviews }: ScriptsTabProps) {
  const [domain, setDomain] = useState('');
  const [category, setCategory] = useState<string>('');
  const [cmpProvider, setCmpProvider] = useState<string>('');
  const [classificationSource, setClassificationSource] = useState<string>('');
  const [beforeConsentOnly, setBeforeConsentOnly] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const filters = useMemo(
    () => ({
      page,
      pageSize,
      domain: domain || undefined,
      category: (category || undefined) as DetectedScript['category'] | undefined,
      consentMode: consentMode === 'all' ? undefined : consentMode,
      gtmContainerId: gtmContainerId === 'all' ? undefined : gtmContainerId,
      cmpProvider: (cmpProvider || undefined) as components['schemas']['CmpProvider'] | undefined,
      classificationSource: (classificationSource || undefined) as
        | components['schemas']['ClassificationSource']
        | undefined,
    }),
    [page, domain, category, consentMode, gtmContainerId, cmpProvider, classificationSource],
  );

  const query = useQuery({
    queryKey: queryKeys.crawls.scripts(crawlId, filters),
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/crawls/{crawlId}/scripts', {
        params: {
          path: { crawlId },
          query: filters,
        },
      });
      if (error) throw error;
      return data;
    },
  });

  const items = (query.data?.items ?? []).filter((s) =>
    beforeConsentOnly ? s.loadedBeforeConsent : true,
  );
  const total = query.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2 rounded-md border bg-background p-2">
        <Input
          placeholder="Domain filter…"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          className="h-8 w-60"
          data-testid="script-domain-filter"
        />
        <Select value={category} onChange={(e) => setCategory(e.target.value)} className="h-8 w-48" data-testid="script-category-filter">
          <option value="">Any category</option>
          {SCRIPT_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
        <Select
          value={classificationSource}
          onChange={(e) => setClassificationSource(e.target.value)}
          className="h-8 w-48"
          data-testid="script-classification-filter"
        >
          <option value="">Any classifier</option>
          {CLASSIFICATION_SOURCES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </Select>
        <Select
          value={cmpProvider}
          onChange={(e) => setCmpProvider(e.target.value)}
          className="h-8 w-40"
          data-testid="script-cmp-filter"
        >
          <option value="">Any CMP</option>
          {CMP_PROVIDERS.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
        <label className="flex items-center gap-1 text-xs">
          <input
            type="checkbox"
            checked={beforeConsentOnly}
            onChange={(e) => setBeforeConsentOnly(e.target.checked)}
            data-testid="script-before-consent"
          />
          Before consent only
        </label>
      </div>

      <Table>
        <THead>
          <TR>
            <TH>Domain</TH>
            <TH>Category</TH>
            <TH>GTM</TH>
            <TH>Initiator</TH>
            <TH>Depth</TH>
            <TH>Pages</TH>
            <TH>Consent</TH>
            <TH>Classifier</TH>
            <TH>Review</TH>
          </TR>
        </THead>
        <TBody>
          {query.isLoading ? (
            <TR><TD colSpan={9} className="text-center text-xs text-muted-foreground">Loading…</TD></TR>
          ) : items.length === 0 ? (
            <TR><TD colSpan={9} className="text-center text-xs text-muted-foreground">No scripts matched.</TD></TR>
          ) : (
            items.map((s) => (
              <ScriptRow key={s.id} script={s} crawlId={crawlId} reviews={reviews} />
            ))
          )}
        </TBody>
      </Table>
      <Pagination page={page} pageCount={pageCount} total={total} onChange={setPage} />
    </div>
  );
}

function ScriptRow({
  script,
  crawlId,
  reviews,
}: {
  script: DetectedScript;
  crawlId: string;
  reviews: ReviewMap;
}) {
  const [open, setOpen] = useState(false);
  const review = reviews.get(`script:${script.id}`);
  return (
    <>
      <TR>
        <TD className="max-w-[280px]">
          <div className="flex items-center gap-1.5">
            <button className="truncate text-left font-mono text-xs hover:underline" onClick={() => setOpen((o) => !o)}>
              {script.domain}
            </button>
            {script.cloakedOwner && (
              <Badge
                variant="warning"
                title={`Resolves to ${script.cloakedOwner} (${script.cloakedTrackerId ?? '?'})`}
                data-testid="script-cloaked-badge"
              >
                cloaked
              </Badge>
            )}
          </div>
        </TD>
        <TD>
          <ScriptCategoryChip category={script.category} cmpProvider={script.cmpProvider ?? undefined} />
        </TD>
        <TD>{script.gtmContainerId ? <Badge variant="info">GTM</Badge> : <span className="text-xs text-muted-foreground">—</span>}</TD>
        <TD className="text-xs">{script.initiatorType ?? '—'}</TD>
        <TD>{script.depth}</TD>
        <TD>
          <Badge variant="muted" title={script.pageIds?.join('\n')}>
            {script.pageCount ?? 1}
          </Badge>
        </TD>
        <TD>
          {script.loadedBeforeConsent ? <Badge variant="destructive">Before</Badge> : <Badge variant="muted">After</Badge>}
          {script.consentMode && <span className="ml-1 text-[10px] text-muted-foreground">{script.consentMode}</span>}
        </TD>
        <TD>
          <span className="text-[10px] uppercase text-muted-foreground">{script.classificationSource ?? '—'}</span>
        </TD>
        <TD>
          <ReviewPopover
            crawlId={crawlId}
            entityType="script"
            entityId={script.id}
            currentStatus={review?.status ?? 'unreviewed'}
            currentNote={review?.note ?? null}
          />
        </TD>
      </TR>
      {open && (
        <TR>
          <TD colSpan={9} className="bg-muted/30">
            <div className="flex flex-col gap-1 text-xs">
              <div className="font-mono">{script.url}</div>
              {script.resolvedCnameChain && script.resolvedCnameChain.length > 1 && (
                <div data-testid="script-cname-chain">
                  <span className="font-semibold">Resolved CNAME chain:</span>
                  <ol className="ml-4 list-decimal">
                    {script.resolvedCnameChain.map((link, idx) => (
                      <li key={`${link}-${idx}`} className="font-mono">
                        {link}
                      </li>
                    ))}
                  </ol>
                </div>
              )}
              {script.callStack && script.callStack.length > 0 && (
                <div>
                  <span className="font-semibold">Call stack:</span>
                  <ol className="ml-4 list-decimal">
                    {script.callStack.map((frame, idx) => (
                      <li key={idx} className="font-mono">
                        {frame.url ?? '(anonymous)'}:{frame.lineNumber ?? '?'}
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          </TD>
        </TR>
      )}
    </>
  );
}

export function Pagination({
  page,
  pageCount,
  total,
  onChange,
}: {
  page: number;
  pageCount: number;
  total: number;
  onChange: (next: number) => void;
}) {
  return (
    <div className="flex items-center justify-between text-xs text-muted-foreground">
      <span>
        Page {page} of {pageCount} · {total} total
      </span>
      <div className="flex gap-1">
        <Button size="sm" variant="outline" onClick={() => onChange(Math.max(1, page - 1))} disabled={page === 1}>
          Previous
        </Button>
        <Button size="sm" variant="outline" onClick={() => onChange(Math.min(pageCount, page + 1))} disabled={page >= pageCount}>
          Next
        </Button>
      </div>
    </div>
  );
}
